import { useCallback, useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import SearchBar from '@/components/feature/SearchBar';

export interface HeroSlide {
  id: string;
  title: string;
  excerpt: string;
  cover: string;
  to: string;
  category: string;
}

export default function HeroCarousel({ slides }: { slides: HeroSlide[] }) {
  const [index, setIndex] = useState(0);
  const touchStartX = useRef(0);
  const count = slides.length;

  const go = useCallback(
    (next: number) => {
      setIndex(((next % count) + count) % count);
    },
    [count],
  );

  useEffect(() => {
    if (count <= 1) return undefined;
    const timer = window.setInterval(() => setIndex((prev) => (prev + 1) % count), 6500);
    return () => window.clearInterval(timer);
  }, [count]);

  if (count === 0) return null;
  const active = slides[index];

  return (
    <section
      className="relative h-[540px] w-full overflow-hidden md:h-[640px]"
      onTouchStart={(event) => {
        touchStartX.current = event.changedTouches[0].clientX;
      }}
      onTouchEnd={(event) => {
        const delta = event.changedTouches[0].clientX - touchStartX.current;
        if (Math.abs(delta) > 50) go(delta < 0 ? index + 1 : index - 1);
      }}
    >
      {slides.map((slide, i) => (
        <div key={slide.id} className={`absolute inset-0 transition-opacity duration-700 ${i === index ? 'opacity-100' : 'opacity-0'}`}>
          <img src={slide.cover} alt={slide.title} title={`${slide.title} 原神焦点资讯`} className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground-950/90 via-foreground-950/65 to-foreground-950/25"></div>
        </div>
      ))}

      <div className="relative z-10 mx-auto flex h-full w-full max-w-7xl flex-col justify-center px-4 md:px-6 lg:px-8">
        <div key={active.id} className="gs-fade-up max-w-2xl">
          <span className="inline-flex items-center gap-1.5 rounded-full border border-background-50/25 bg-background-50/10 px-3 py-1 text-xs font-label text-background-50 backdrop-blur-sm">
            <i className="ri-fire-line text-accent-300"></i>
            {active.category}
          </span>
          <h1 className="mt-4 font-heading text-2xl font-black leading-tight text-background-50 md:text-5xl md:leading-tight">
            {active.title}
          </h1>
          <p className="mt-4 hidden max-w-xl text-sm leading-relaxed text-background-50/80 md:block md:text-base">{active.excerpt}</p>

          <div className="mt-6 max-w-xl">
            <SearchBar variant="hero" />
          </div>

          <div className="mt-6 flex flex-wrap items-center gap-3">
            <Link
              to={active.to}
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-label font-medium text-foreground-950 transition-colors hover:bg-accent-400"
            >
              阅读全文
              <i className="ri-arrow-right-line"></i>
            </Link>
            <Link
              to="/news"
              className="inline-flex cursor-pointer items-center gap-1.5 whitespace-nowrap rounded-md border border-background-50/40 px-5 py-2.5 text-sm font-label text-background-50 transition-colors hover:bg-background-50/10"
            >
              <i className="ri-newspaper-line"></i>
              全部资讯
            </Link>
          </div>
        </div>
      </div>

      {count > 1 ? (
        <>
          <button
            type="button"
            aria-label="上一张"
            onClick={() => go(index - 1)}
            className="absolute left-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/60 md:flex lg:left-6"
          >
            <i className="ri-arrow-left-s-line text-2xl"></i>
          </button>
          <button
            type="button"
            aria-label="下一张"
            onClick={() => go(index + 1)}
            className="absolute right-3 top-1/2 z-10 hidden h-10 w-10 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-background-50/30 bg-foreground-950/30 text-background-50 backdrop-blur-sm transition-colors hover:bg-foreground-950/60 md:flex lg:right-6"
          >
            <i className="ri-arrow-right-s-line text-2xl"></i>
          </button>

          <div className="absolute bottom-6 left-1/2 z-10 flex -translate-x-1/2 items-center gap-2">
            {slides.map((slide, i) => (
              <button
                key={slide.id}
                type="button"
                aria-label={`切换到第 ${i + 1} 张`}
                onClick={() => go(i)}
                className={`h-1.5 cursor-pointer rounded-full transition-all ${i === index ? 'w-7 bg-accent-500' : 'w-1.5 bg-background-50/50'}`}
              />
            ))}
          </div>
        </>
      ) : null}
    </section>
  );
}